import React, {
    forwardRef,
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { useReactToPrint } from "react-to-print";
import { FaRegArrowAltCircleRight } from "react-icons/fa";
import { VoucherContext } from "../../../../../../context/VoucherEntryContext";
import { Typography } from "@mui/material";
import { fetchDataFromApi, postDataToTheServer } from "../../../../../../helpers/api";
import AppURL from "../../../../../../constants/AppURL";
import Container from "../../../../../../constants/Container";
import AccountWatermarks from "../../../../../../constants/AccountWatermarks";
import FullLedgerDetailsView from "./FullLedgerDetailsView";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/store";
import { formatMoney_2 } from "../../../../../../helpers/DateFormat";
import PrintSingleVoucherView from "../print/PrintLedgerView";
import PrintLedgerView from "../print/PrintLedgerView";

// Main Ledger Full View Component
const FullLedgersView = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [onEdit, setOnEdit] = useState({ edit: false, ledger: [] });
    const [ledgerList, setLedgerList] = useState<any>([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [ledgerOptions, setLedgerOptions] = useState({
        date_ne: "",
        open: false,
    });
    const [singleVoucherData, setSingleVoucherData] = useState([]);
    const printComponentRef = useRef<HTMLDivElement>(null);
    const printLedgerRef = useRef<HTMLDivElement>(null);

    // Then get the context

    const context = useContext(VoucherContext);
    const { SOFTWARE_ORG, SET_FULL_SOFT_DATA } = useSelector((state: RootState) => state.software);

    // 3. Move all effects and callbacks after hooks but before any conditional returns
    const fetchLedgerView = useCallback(async () => {
        if (!context) return;
        const isDetails = parseInt(context.values?.glid) === 90 ? 1 : 0;
        setIsLoading(true);
        const formData = new FormData();
        formData.append("sglsn", context.values?.sglsn);
        formData.append("user_id", "0");
        formData.append("fiscal_start", context.values.ledger_start_date || "");
        formData.append("date_ne", context.values.ledger_end_date || "");
        formData.append("member_sn", context.values?.member_sn || "0");
        formData.append("details_mode", isDetails?.toString());
        try {
            const res = await postDataToTheServer(
                AppURL.AccountURL + "account/ledger/data/list/create/view/",
                formData
            );
            setLedgerList(res);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [context]);

    useEffect(() => {
        if (context) {
            fetchLedgerView();
        }
    }, [
        context?.values?.view_full_leader,
        context?.values?.date_ne,
        context?.values?.member_sn,
        context?.values?.sglsn,
        fetchLedgerView
    ]);
    // // Print voucher using react-to-print
    const handlePrintSingleVoucher = useReactToPrint({
        content: () => printComponentRef.current,
    });
    const handlePrintLedger = useReactToPrint({
        content: () => printLedgerRef.current,
    });
    // 4. Now do the early return check
    if (!context) {
        return <Typography>Error: Context not available</Typography>;
    }

    const { values, setFieldValue, ResetForm, handleChange, handleBlur, touched, errors } = context;

    // // Render component for single voucher printing (using forwardRef)
    const RenderSingleVoucherComponent = forwardRef<HTMLDivElement>((props, ref) => (
        <div ref={ref}>
            <PrintSingleVoucherView ledgerList={ledgerList?.results}
      date_ne={ledgerOptions?.date_ne} />
        </div>
    ));

    // // If we're in edit mode, render the edit form component
    // if (onEdit?.edit) {
    //     return <EditLedgerGetView onEdit={onEdit} setOnEdit={setOnEdit} />;
    // }

    const RenderLedgerComponent = React.forwardRef(() => (
        <PrintLedgerView ledgerList={ledgerList?.results}
      date_ne={ledgerOptions?.date_ne} />
    ));

    const totalCreditAmt = ledgerList?.data?.reduce(
        (sum: Number, item: any) => sum + (item.cramt ? item.cramt : 0),
        0
    );
    const totalDebitAmt = ledgerList?.data?.reduce((acc: Number, item: any) => {
        return acc + (item?.dramt ? item?.dramt : 0);
    }, 0);

    const totalDebitCreditAmt = ledgerList?.data?.reduce((acc: Number, item: any) => {
        return acc + (item?.subaccbal ? item?.subaccbal : 0);
    }, 0);

    return (
        <Container padding>
            <Fragment>
                {ledgerOptions?.open ? (
                    <Fragment>
                        <FullLedgerDetailsView
                            setOnEdit={setOnEdit}
                            onLedgerOpt={ledgerOptions}
                            setOnLedgerOpt={setLedgerOptions}
                        />
                    </Fragment>
                ) : (
                    <Fragment>
                        <div className="flex justify-between mb-2 gap-3 relative">
                            <div className="flex gap-2">
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        name="ledger_start_date"
                                        onChange={(e) =>
                                            setFieldValue("ledger_start_date", e.target.value)
                                        }
                                        value={values.ledger_start_date}
                                        className="ring-1 ring-slate-400 px-2 py-0.5 font-public-sans font-normal text-[12px] focus:ring-green-600 outline-none rounded-sm"
                                        placeholder="Enter From Date"
                                    />
                                    <h6>-</h6>
                                    <input
                                        type="text"
                                        name="ledger_end_date"
                                        onChange={(e) =>
                                            setFieldValue("ledger_end_date", e.target.value)
                                        }
                                        value={values?.ledger_end_date}
                                        className="ring-1 ring-slate-400 px-2 py-0.5 font-public-sans font-normal text-[12px] focus:ring-green-600 outline-none rounded-sm"
                                        placeholder="Enter To Date"
                                    />
                                </div>
                                <button
                                    onClick={fetchLedgerView}
                                    className="py-0.5 px-3 bg-green-600 hover:bg-opacity-80 text-white text-[12px] font-public-sans rounded-sm"
                                >
                                    Load Ledger
                                </button>
                            </div>
                            <div className="flex justify-end gap-3">
                                {/* )} */}
                                <button
                                    onClick={handlePrintLedger}
                                    className="py-0.5 px-3 hover:bg-teal-500 bg-teal-600 text-white text-[12px] font-public-sans rounded-sm"
                                >
                                    Print Ledger
                                </button>
                                <button
                                    onClick={() => setFieldValue("view_full_leader", false)}
                                    className="py-0.5 px-3 hover:bg-red-300 bg-red-400 text-white text-[12px] font-public-sans rounded-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                        <AccountWatermarks text="Ledgers">
                            <div className="relative overflow-x-auto overflow-y-auto h-[60vh]">
                                <table className="w-full text-sm text-left whitespace-nowrap rtl:text-right font-public-sans rounded-md text-slate500 dark:text-slate-400">
                                    <thead className="text-[12px] text-slate-700 capitalize font-public-sans rounded-t-md dark:text-slate-400">
                                        <tr className="top-0 z-0 sticky bg-slate-200 dark:bg-slate-800">
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700 w-16"
                                            >
                                                S.N.
                                            </th>
                                            <th
                                                scope="col"
                                                align="left"
                                                className="px-2 py-0.5 border dark:border-slate-700"
                                            >
                                                Date
                                            </th>
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700 w-16"
                                            >
                                                Bill No
                                            </th>
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700"
                                            >
                                                TSN
                                            </th>
                                            <th
                                                scope="col"
                                                align="left"
                                                className="px-2 py-0.5 border dark:border-slate-700"
                                            >
                                                Particular Name
                                            </th>
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700"
                                            >
                                                Debit Amount
                                            </th>
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700"
                                            >
                                                Credit Amount
                                            </th>
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700"
                                            >
                                                Sub Ac Balance
                                            </th>
                                            <th
                                                scope="col"
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate-700 w-10"
                                            >
                                                Dr/Cr
                                            </th>
                                            {parseInt(values.glid) !== 90 && (
                                                <th
                                                    scope="col"
                                                    align="center"
                                                    className="px-2 py-0.5 border dark:border-slate-700 w-10"
                                                >
                                                    Action
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr className="bg-white dark:bg-slate900 cursor-pointer select-none hover:bg-slate100 border-b font-public-sans font-medium text-[12px] text-gray800 dark:text-slate300 dark:border-slate700">
                                                <td
                                                    colSpan={10}
                                                    align="center"
                                                    className="px-2 py-0.5 whitespace-nowrap dark:text-white border font-normal dark:border-slate700"
                                                >
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : (
                                            ledgerList?.results?.map((ledger: any, index: any) => (
                                                <tr
                                                    key={index}
                                                    onClick={() => setSelectedRow(index)}
                                                    className={`${selectedRow === index
                                                        ? "bg-green-100"
                                                        : "bg-white hover:bg-slate100"
                                                        } dark:bg-slate900 cursor-pointer select-none border-b font-public-sans font-normal text-[12px] text-gray800 dark:text-slate300 dark:border-slate700`}
                                                >
                                                    <td
                                                        align="center"
                                                        className="px-2 py-0.5 border dark:border-slate700"
                                                    >
                                                        {index + 1}.
                                                    </td>
                                                    <td className="px-2 py-0.5 border dark:border-slate700">
                                                        {ledger?.dated}
                                                    </td>
                                                    <td
                                                        align="center"
                                                        className="px-2 py-0.5 border dark:border-slate700 font-normal"
                                                    >
                                                        {ledger?.billrno ?? 0}
                                                    </td>
                                                    <td
                                                        align="center"
                                                        className="px-2 py-0.5 border dark:border-slate700 font-normal"
                                                        onClick={async () => {
                                                            try {
                                                                const res = await fetchDataFromApi(
                                                                    AppURL.AccountURL +
                                                                    "account/single/voucher/list/report/view/?tsn=" +
                                                                    ledger?.tsn
                                                                );
                                                                setSingleVoucherData(res);
                                                            } catch (error) {
                                                                console.error(error);
                                                            }
                                                        }}
                                                    >
                                                        {ledger?.tsn ?? ""}
                                                    </td>
                                                    <td className="px-2 py-0.5 border dark:border-slate700">
                                                        {ledger?.particulars}
                                                    </td>
                                                    <td
                                                        align="right"
                                                        className="px-2 py-0.5 border dark:border-slate700"
                                                    >
                                                        {ledger?.dramt
                                                            ? formatMoney_2(ledger?.dramt)
                                                            : formatMoney_2(0)}
                                                    </td>
                                                    <td
                                                        align="right"
                                                        className="px-2 py-0.5 border dark:border-slate700"
                                                    >
                                                        {ledger?.cramt
                                                            ? formatMoney_2(ledger?.cramt)
                                                            : formatMoney_2(0)}
                                                    </td>
                                                    <td
                                                        align="right"
                                                        className="px-2 py-0.5 border dark:border-slate700"
                                                    >
                                                        {ledger?.subaccbal
                                                            ? formatMoney_2(ledger?.subaccbal)
                                                            : formatMoney_2(0)}
                                                    </td>
                                                    <td
                                                        align="center"
                                                        className="px-2 py-0.5 border dark:border-slate700"
                                                    >
                                                        {ledger?.subdrcr}
                                                    </td>
                                                    {parseInt(values.glid) !== 90 && (
                                                        <td
                                                            align="center"
                                                            className="px-2 py-0.5 border dark:border-slate700"
                                                            onClick={() =>
                                                                setLedgerOptions({
                                                                    date_ne: ledger?.dated,
                                                                    open: true,
                                                                })
                                                            }
                                                        >
                                                            <FaRegArrowAltCircleRight className="my-auto" />
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                        <tr
                                            className={`bg-slate-100  font-public-sans font-semibold text-[12px] text-gray800`}
                                        >
                                            <td
                                                align="right" colSpan={5}
                                                className="px-2 py-0.5 border dark:border-slate700"
                                            >Total Amount</td>
                                            <td
                                                align="right"
                                                className="px-2 py-0.5 border dark:border-slate700"
                                            >
                                                {formatMoney_2(totalDebitAmt || 0)}
                                            </td>
                                            <td
                                                align="right"
                                                className="px-2 py-0.5 border dark:border-slate700"
                                            >
                                                {formatMoney_2(totalCreditAmt || 0)}
                                            </td>
                                            <td
                                                align="right"
                                                className="px-2 py-0.5 border dark:border-slate700"
                                            >
                                                {formatMoney_2(totalDebitCreditAmt || 0)}
                                            </td>
                                            <td
                                                align="center"
                                                className="px-2 py-0.5 border dark:border-slate700"
                                            ></td>
                                            {
                                                parseInt(values.glid) !== 90 && <td
                                                    align="center"
                                                    className="px-2 py-0.5 border dark:border-slate700"
                                                ></td>
                                            }
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </AccountWatermarks>
                        {/* Hidden printing component */}
                        <div className="hidden">
                            <div ref={printComponentRef}>
                                <RenderSingleVoucherComponent />
                            </div>
                        </div>

                        <div className="hidden">
                            <div ref={printLedgerRef}>
                                <RenderLedgerComponent />
                            </div>
                        </div>
                    </Fragment>
                )}
            </Fragment>
        </Container>
    );
};

export default FullLedgersView;
